import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { PlugZap, RefreshCw, Search, Loader2, Zap, Wifi, WifiOff, MapPin, Link2, Unlink, Play, Square, SlidersHorizontal, Eye, Settings2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { StationDetailDialog } from "@/components/ev/StationDetailDialog";
import { StationAdminDialog, RegisterStationDialog } from "@/components/ev/StationAdminDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATO = {
  in_ricarica: { label: "In ricarica", cls: "bg-emerald-100 text-emerald-700 ring-emerald-200" },
  disponibile: { label: "Disponibile", cls: "bg-sky-100 text-sky-700 ring-sky-200" },
  in_sosta: { label: "In sosta", cls: "bg-amber-100 text-amber-700 ring-amber-200" },
  errore: { label: "Errore", cls: "bg-red-100 text-red-700 ring-red-200" },
  offline: { label: "Offline", cls: "bg-slate-100 text-slate-500 ring-slate-200" },
};
const badge = (s) => STATO[s] || { label: s || "—", cls: "bg-slate-100 text-slate-600 ring-slate-200" };
const kw = (w) => (w == null ? "—" : `${(w / 1000).toFixed(1)} kW`);

function Kpi({ label, value, testid }) {
  return (
    <Card className="flex flex-col gap-1 p-4" data-testid={testid}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-2xl font-extrabold tabular-nums">{value}</span>
    </Card>
  );
}

function LinkDialog({ station, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get("/ev/plants-search", { params: { q } });
        setResults(res.data?.plants || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const pick = async (plant) => {
    setBusy(true);
    try {
      await api.post(`/ev/stations/${station.id}/link`, { plant_id: plant.id });
      toast.success(`Collegata a ${plant.nome}`);
      onLinked();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Collegamento non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ev-link-dialog">
        <DialogHeader>
          <DialogTitle>Collega «{station.nome || station.ocpp_id}» a un impianto</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca impianto per nome…"
            className="pl-9" data-testid="ev-link-search" />
        </div>
        <div className="max-h-72 space-y-1 overflow-auto">
          {q.trim().length >= 2 && results.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">Nessun impianto trovato.</p>
          )}
          {results.map((p) => (
            <button key={p.id} disabled={busy} onClick={() => pick(p)} data-testid={`ev-link-plant-${p.id}`}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted disabled:opacity-50">
              <span className="font-medium">{p.nome}</span>
              <span className="text-xs text-muted-foreground">{[p.comune, p.provincia].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommandDialog({ station, onClose }) {
  const [power, setPower] = useState(null);
  const [cid, setCid] = useState(1);
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);
  const loadPower = async () => {
    try { const r = await api.get(`/ev/stations/${station.id}/power`); setPower(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { loadPower(); /* eslint-disable-next-line */ }, []);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); loadPower(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Comando non riuscito"); }
    finally { setBusy(false); }
  };
  const rows = power ? Object.entries(power).filter(([, v]) => ["number", "string", "boolean"].includes(typeof v)) : [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ev-cmd-dialog">
        <DialogHeader><DialogTitle>Comandi · {station.nome || station.ocpp_id}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-muted p-3 text-xs" data-testid="ev-cmd-power">
            {power ? (
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {rows.length ? rows.map(([k, v]) => <span key={k}>{k}: <b>{String(v)}</b></span>) : <span className="text-muted-foreground">Nessun dato limite</span>}
              </div>
            ) : <span className="text-muted-foreground">Lettura limiti…</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-muted-foreground">Connettore</label>
            <Input type="number" value={cid} onChange={(e) => setCid(Number(e.target.value) || 1)} className="w-20" data-testid="ev-cmd-connector" />
            <Button onClick={() => act(() => api.post(`/ev/stations/${station.id}/start`, { connector_id: cid }), "Ricarica avviata")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ev-cmd-start"><Play className="mr-1 h-4 w-4" />Avvia</Button>
            <Button onClick={() => act(() => api.post(`/ev/stations/${station.id}/stop`, { connector_id: cid }), "Ricarica fermata")} disabled={busy} variant="destructive" data-testid="ev-cmd-stop"><Square className="mr-1 h-4 w-4" />Ferma</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" placeholder="Limite in Watt" value={limit} onChange={(e) => setLimit(e.target.value)} className="w-40" data-testid="ev-cmd-limit-input" />
            <Button variant="outline" onClick={() => act(() => api.put(`/ev/stations/${station.id}/power-limit`, { limit_w: limit === "" ? null : Number(limit) }), "Limite impostato")} disabled={busy} data-testid="ev-cmd-set-limit"><SlidersHorizontal className="mr-1 h-4 w-4" />Imposta limite</Button>
            <Button variant="ghost" onClick={() => act(() => api.post(`/ev/stations/${station.id}/power/verify`), "Verifica richiesta")} disabled={busy} data-testid="ev-cmd-verify">Verifica</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EvMonitoringPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [linkFor, setLinkFor] = useState(null);
  const [cmdFor, setCmdFor] = useState(null);
  const [detFor, setDetFor] = useState(null);
  const [adminFor, setAdminFor] = useState(null);
  const [showRegister, setShowRegister] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.get("/ev/stations");
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossibile contattare AURA EV");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const unlink = async (station) => {
    try {
      await api.delete(`/ev/stations/${station.id}/link`);
      toast.success("Collegamento rimosso");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const stations = data?.stations || [];
  const kpi = data?.kpi || {};
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return stations;
    return stations.filter((s) => [s.nome, s.ocpp_id, s.serial, s.indirizzo, s.owner_email, s.plant_name]
      .some((v) => (v || "").toLowerCase().includes(t)));
  }, [stations, q]);

  if (!user) return null;
  if (!["admin", "head_of_partner", "back_office", "technical"].includes(user.role) && !["back_office", "technical"].includes(user.role_actual) && !(user.extra_pages || []).includes("monitoring"))
    return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 p-4 sm:p-8" data-testid="ev-monitoring-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            <PlugZap className="h-8 w-8 text-primary" />EV Monitoring
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Stazioni di ricarica AURA collegate alla rete Torri.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="ev-refresh-btn">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Aggiorna
        </Button>
        <Button onClick={() => setShowRegister(true)} data-testid="ev-register-btn">
          <Plus className="mr-2 h-4 w-4" />Registra stazione
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi label="Stazioni" value={kpi.totale ?? "—"} testid="ev-kpi-totale" />
        <Kpi label="Online" value={kpi.online ?? "—"} testid="ev-kpi-online" />
        <Kpi label="In ricarica" value={kpi.in_ricarica ?? "—"} testid="ev-kpi-ricarica" />
        <Kpi label="Offline" value={kpi.offline ?? "—"} testid="ev-kpi-offline" />
        <Kpi label="Collegate a impianto" value={kpi.collegate_impianto ?? "—"} testid="ev-kpi-collegate" />
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, seriale, indirizzo, cliente…"
          className="pl-9" data-testid="ev-search-input" />
      </div>

      {err && <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="ev-error">{err}</Card>}

      {loading && !data ? (
        <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Caricamento stazioni…</div>
      ) : (
        <div className="grid gap-3" data-testid="ev-stations-list">
          {filtered.length === 0 && !err && (
            <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="ev-empty">Nessuna stazione trovata.</Card>
          )}
          {filtered.map((s) => {
            const b = badge(s.stato);
            return (
              <Card key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`ev-station-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-base font-bold" data-testid="ev-station-name">{s.nome || s.ocpp_id}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${b.cls}`} data-testid="ev-station-stato">{b.label}</span>
                    {s.online
                      ? <Wifi className="h-4 w-4 text-emerald-500" title="Online" />
                      : <WifiOff className="h-4 w-4 text-slate-400" title="Offline" />}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>OCPP {s.ocpp_id}</span>
                    {s.serial && <span>S/N {s.serial}</span>}
                    {s.modello && <span>{s.modello}</span>}
                    {s.indirizzo && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.indirizzo}</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                    {s.plant_ref ? (
                      <>
                        <span className="inline-flex items-center gap-1 font-medium text-emerald-600" data-testid="ev-station-collegata">
                          <Link2 className="h-3 w-3" />Impianto: {s.plant_name || s.plant_ref}
                        </span>
                        {s.plant_link_torri && (
                          <button onClick={() => unlink(s)} data-testid={`ev-unlink-${s.id}`}
                            className="inline-flex items-center gap-1 text-slate-400 transition-colors hover:text-red-500">
                            <Unlink className="h-3 w-3" />Scollega
                          </button>
                        )}
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkFor(s)} data-testid={`ev-link-btn-${s.id}`}>
                        <Link2 className="mr-1 h-3 w-3" />Collega a impianto
                      </Button>
                    )}
                    {s.owner_email && <span className="text-muted-foreground">{s.owner_email}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-right">
                  <div className="flex items-center gap-1.5">
                    <Zap className="h-5 w-5 text-primary" />
                    <span className="text-xl font-extrabold tabular-nums" data-testid="ev-station-potenza">{kw(s.potenza_w)}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setDetFor(s)} data-testid={`ev-detail-btn-${s.id}`}>
                    <Eye className="mr-1 h-4 w-4" />Dettaglio
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setCmdFor(s)} data-testid={`ev-cmd-btn-${s.id}`}>
                    <SlidersHorizontal className="mr-1 h-4 w-4" />Comandi
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setAdminFor(s)} data-testid={`ev-admin-btn-${s.id}`}>
                    <Settings2 className="mr-1 h-4 w-4" />Gestione
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {linkFor && <LinkDialog station={linkFor} onClose={() => setLinkFor(null)} onLinked={load} />}
      {cmdFor && <CommandDialog station={cmdFor} onClose={() => setCmdFor(null)} />}
      {detFor && <StationDetailDialog station={detFor} onClose={() => setDetFor(null)} />}
      {adminFor && <StationAdminDialog station={adminFor} onClose={() => setAdminFor(null)} onSaved={load} />}
      {showRegister && <RegisterStationDialog onClose={() => setShowRegister(false)} onDone={load} />}
    </div>
  );
}
